import type { TFunction } from 'i18next';

export type FaqVisualType =
  | 'none'
  | 'hero'
  | 'projects'
  | 'workspace'
  | 'ai'
  | 'chat'
  | 'gallery'
  | 'media'
  | 'search'
  | 'profile'
  | 'settings'
  | 'security'
  | 'calendar'
  | 'community'
  | 'workflow'
  | 'platform'
  | 'editor'
  | 'render'
  | 'calls';

export interface FaqItem {
  id: string;
  categoryId: string;
  question: string;
  paragraphs: string[];
  keywords: string;
  visual: FaqVisualType;
  learnMore?: { to: string; label: string };
  relatedIds: string[];
}

export interface FaqItemDef {
  id: string;
  categoryId: string;
  visual: FaqVisualType;
  pCount: number;
  learnMoreTo?: string;
  relatedIds?: string[];
}

export const FAQ_CATEGORIES = [
  { id: 'all', label: 'All topics', icon: '✦' },
  { id: 'getting-started', label: 'Getting started', icon: '🌐' },
  { id: 'workspace', label: 'Workspace', icon: '📋' },
  { id: 'ai', label: 'AI', icon: '✨' },
  { id: 'media', label: 'Media', icon: '🖼️' },
  { id: 'security', label: 'Security', icon: '🛡️' },
  { id: 'settings', label: 'Settings', icon: '⚙️' },
  { id: 'platform', label: 'Platform', icon: '🏠' },
] as const;

export const FAQ_ITEM_DEFS: FaqItemDef[] = [
  { id: 'saas-what-is-saas', categoryId: 'getting-started', visual: 'hero', pCount: 4 },
  { id: 'saas-create-site-project', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-projects-list', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-project-trash-restore', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-choose-template', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-template-preview', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-apply-template', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-edit-site-sections', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-component-library', categoryId: 'workspace', visual: 'editor', pCount: 4 },
  { id: 'saas-responsive-preview', categoryId: 'workspace', visual: 'editor', pCount: 4 },
  { id: 'saas-publish-public-site', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-public-url-slug', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-custom-domain-dns', categoryId: 'workspace', visual: 'platform', pCount: 4 },
  { id: 'saas-domain-verification', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-orgs-overview', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-org-members', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-subscription-plans', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-billing-limits', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-ai-site-structure', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-ai-hero-copy', categoryId: 'workspace', visual: 'ai', pCount: 4 },
  { id: 'saas-ai-seo-meta', categoryId: 'workspace', visual: 'ai', pCount: 4 },
  { id: 'saas-ai-color-palette', categoryId: 'workspace', visual: 'ai', pCount: 4 },
  { id: 'saas-ai-hero-image', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-media-picker', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-form-blocks', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-analytics-embed', categoryId: 'workspace', visual: 'settings', pCount: 4 },
  { id: 'saas-version-history', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-projects-trash-restore', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-template-catalog', categoryId: 'workspace', visual: 'gallery', pCount: 4 },
  { id: 'saas-template-manage-slug', categoryId: 'workspace', visual: 'editor', pCount: 4 },
  { id: 'saas-public-site-slug', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-orgs-members-roles', categoryId: 'workspace', visual: 'workspace', pCount: 4 },
  { id: 'saas-plans-subscription', categoryId: 'workspace', visual: 'settings', pCount: 4 },
  { id: 'saas-ai-structure-generator', categoryId: 'workspace', visual: 'ai', pCount: 4 },
  { id: 'saas-form-blocks-submit', categoryId: 'workspace', visual: 'workspace', pCount: 4 },
  { id: 'saas-version-history-sites', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-admin-saas', categoryId: 'workspace', visual: 'platform', pCount: 4 },
  { id: 'saas-create-site-project-fields', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-choose-template-filter', categoryId: 'workspace', visual: 'gallery', pCount: 4 },
  { id: 'saas-apply-template-overwrite', categoryId: 'workspace', visual: 'editor', pCount: 4 },
  { id: 'saas-edit-site-sections-nav', categoryId: 'workspace', visual: 'editor', pCount: 4 },
  { id: 'saas-publish-staging-preview', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-public-url-slug-change', categoryId: 'workspace', visual: 'settings', pCount: 4 },
  { id: 'saas-domain-verification-txt', categoryId: 'workspace', visual: 'platform', pCount: 4 },
  { id: 'saas-org-invite-pending', categoryId: 'workspace', visual: 'workspace', pCount: 4 },
  { id: 'saas-billing-limits-sites', categoryId: 'workspace', visual: 'settings', pCount: 4 },
  { id: 'saas-ai-hero-image-replace', categoryId: 'workspace', visual: 'ai', pCount: 4 },
  { id: 'saas-media-picker-stock', categoryId: 'workspace', visual: 'media', pCount: 4 },
  { id: 'saas-form-spam-captcha', categoryId: 'workspace', visual: 'workspace', pCount: 4 },
  { id: 'saas-version-history-compare', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-template-manage-permissions', categoryId: 'workspace', visual: 'platform', pCount: 4 },
  { id: 'saas-seo-sitemap-auto', categoryId: 'workspace', visual: 'settings', pCount: 4 },
  { id: 'saas-custom-code-block', categoryId: 'workspace', visual: 'editor', pCount: 4 },
  { id: 'saas-org-switcher', categoryId: 'workspace', visual: 'workspace', pCount: 4 },
  { id: 'saas-maintenance-saas-publish', categoryId: 'workspace', visual: 'platform', pCount: 4 },
  { id: 'saas-ssl-cert-renewal', categoryId: 'workspace', visual: 'platform', pCount: 4 },
  { id: 'saas-project-trash-retention', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-ai-color-contrast-check', categoryId: 'workspace', visual: 'ai', pCount: 4 },
  { id: 'saas-responsive-tablet-breakpoint', categoryId: 'workspace', visual: 'editor', pCount: 4 },
  { id: 'saas-component-faq-block', categoryId: 'workspace', visual: 'editor', pCount: 4 },
  { id: 'saas-analytics-realtime-delay', categoryId: 'workspace', visual: 'settings', pCount: 4 },
  { id: 'saas-multi-language-sites', categoryId: 'workspace', visual: 'editor', pCount: 4 },
  { id: 'saas-admin-impersonate-audit', categoryId: 'workspace', visual: 'security', pCount: 4 },
  { id: 'saas-site-password-protect', categoryId: 'workspace', visual: 'settings', pCount: 4 },
  { id: 'saas-redirect-rules', categoryId: 'workspace', visual: 'settings', pCount: 4 },
  { id: 'saas-team-comments-editor', categoryId: 'workspace', visual: 'workspace', pCount: 4 },
  { id: 'saas-webhook-publish-hook', categoryId: 'workspace', visual: 'platform', pCount: 4 },
  { id: 'saas-backup-html-export', categoryId: 'workspace', visual: 'projects', pCount: 4 },
  { id: 'saas-robots-txt-editor', categoryId: 'workspace', visual: 'settings', pCount: 4 },
  { id: 'saas-favicon-upload', categoryId: 'workspace', visual: 'editor', pCount: 4 },
  { id: 'saas-portal-sso', categoryId: 'platform', visual: 'platform', pCount: 4 },
  { id: 'saas-login-troubleshoot', categoryId: 'platform', visual: 'platform', pCount: 4 },
  { id: 'saas-language-i18n', categoryId: 'platform', visual: 'platform', pCount: 4 },
  { id: 'saas-data-storage', categoryId: 'platform', visual: 'platform', pCount: 4 },
  { id: 'saas-privacy-overview', categoryId: 'security', visual: 'security', pCount: 4 },
  { id: 'saas-report-block', categoryId: 'security', visual: 'security', pCount: 4 },
  { id: 'saas-moderation-overview', categoryId: 'security', visual: 'security', pCount: 4 },
  { id: 'saas-admin-overview', categoryId: 'platform', visual: 'platform', pCount: 4 },
  { id: 'saas-notifications', categoryId: 'settings', visual: 'settings', pCount: 4 },
  { id: 'saas-settings-overview', categoryId: 'settings', visual: 'settings', pCount: 4 },
  { id: 'saas-media-library', categoryId: 'media', visual: 'media', pCount: 4 },
  { id: 'saas-upload-basics', categoryId: 'media', visual: 'media', pCount: 4 },
  { id: 'saas-ai-overview', categoryId: 'ai', visual: 'ai', pCount: 4 },
  { id: 'saas-ai-safety', categoryId: 'ai', visual: 'ai', pCount: 4 },
  { id: 'saas-keyboard-shortcuts', categoryId: 'platform', visual: 'platform', pCount: 4 },
  { id: 'saas-navigation-overview', categoryId: 'platform', visual: 'platform', pCount: 4 },
  { id: 'saas-mobile-responsive', categoryId: 'platform', visual: 'platform', pCount: 4 },
  { id: 'saas-offline-errors', categoryId: 'platform', visual: 'platform', pCount: 4 },
  { id: 'saas-faq-search', categoryId: 'platform', visual: 'platform', pCount: 4 },
  { id: 'saas-diagnostics', categoryId: 'platform', visual: 'platform', pCount: 4 },
  { id: 'saas-updates-maintenance', categoryId: 'platform', visual: 'platform', pCount: 4 },
  { id: 'saas-permissions-roles', categoryId: 'platform', visual: 'platform', pCount: 4 },
  { id: 'saas-export-data', categoryId: 'platform', visual: 'platform', pCount: 4 },
  { id: 'saas-account-delete', categoryId: 'security', visual: 'security', pCount: 4 },
  { id: 'saas-performance-tips', categoryId: 'platform', visual: 'platform', pCount: 4 },
  { id: 'saas-accessibility', categoryId: 'platform', visual: 'platform', pCount: 4 },
  { id: 'saas-backup-restore', categoryId: 'platform', visual: 'platform', pCount: 4 },
  { id: 'saas-incident-response', categoryId: 'security', visual: 'security', pCount: 4 },
  { id: 'saas-rate-limits', categoryId: 'platform', visual: 'platform', pCount: 4 },
  { id: 'saas-cookie-consent', categoryId: 'platform', visual: 'platform', pCount: 4 },
];

export const FAQ_POPULAR_IDS = [
  'saas-what-is-saas', 'saas-create-site-project', 'saas-publish-public-site', 'saas-custom-domain-dns', 'saas-portal-sso', 'saas-ai-site-structure',
] as const;

export interface FaqShortcutEntry {
  chord: string;
  labelKey: string;
}

export interface FaqShortcutGroup {
  key: string;
  titleKey: string;
  items: FaqShortcutEntry[];
}

export const FAQ_SHORTCUT_GROUPS: FaqShortcutGroup[] = [
  // no shortcut groups
];

export const FAQ_WORKFLOW_STEPS = [
  { step: 1, itemId: 'saas-create-site-project', icon: '🌐' },
  { step: 2, itemId: 'saas-choose-template', icon: '📋' },
  { step: 3, itemId: 'saas-edit-site-sections', icon: '✏️' },
  { step: 4, itemId: 'saas-ai-seo-meta', icon: '✨' },
  { step: 5, itemId: 'saas-publish-public-site', icon: '🚀' },
  { step: 6, itemId: 'saas-data-storage', icon: '💾' },
] as const;

function itemBaseKey(id: string) {
  return `faq.items.${id}`;
}

export function getFaqItemDef(id: string): FaqItemDef | undefined {
  return FAQ_ITEM_DEFS.find((d) => d.id === id);
}

export function resolveFaqItemById(t: TFunction, id: string): FaqItem | null {
  const def = getFaqItemDef(id);
  return def ? resolveFaqItem(t, def) : null;
}

export function resolveFaqItem(t: TFunction, def: FaqItemDef): FaqItem {
  const base = itemBaseKey(def.id);
  const paragraphs: string[] = [];
  for (let i = 0; i < def.pCount; i++) {
    paragraphs.push(t(`${base}.p${i}`));
  }
  const learnMore =
    def.learnMoreTo !== undefined
      ? { to: def.learnMoreTo, label: t(`${base}.learnMoreLabel`) }
      : undefined;
  return {
    id: def.id,
    categoryId: def.categoryId,
    question: t(`${base}.question`),
    paragraphs,
    keywords: t(`${base}.keywords`),
    visual: def.visual,
    learnMore,
    relatedIds: def.relatedIds ?? [],
  };
}

export function faqItemCountByCategory(): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const def of FAQ_ITEM_DEFS) {
    counts[def.categoryId] = (counts[def.categoryId] ?? 0) + 1;
  }
  return counts;
}

export function searchFaqItems(t: TFunction, query: string, categoryId: string): FaqItem[] {
  const q = query.trim().toLowerCase();
  const defs =
    categoryId === 'all'
      ? FAQ_ITEM_DEFS
      : FAQ_ITEM_DEFS.filter((d) => d.categoryId === categoryId);
  const items = defs.map((d) => resolveFaqItem(t, d));
  if (!q) return items;
  return items.filter(
    (item) =>
      item.question.toLowerCase().includes(q) ||
      item.keywords.toLowerCase().includes(q) ||
      item.paragraphs.some((p) => p.toLowerCase().includes(q)),
  );
}

export function buildFaqHref(opts: { id?: string; cat?: string; q?: string }): string {
  const params = new URLSearchParams();
  if (opts.id) params.set('id', opts.id);
  else if (opts.cat) params.set('cat', opts.cat);
  else if (opts.q) params.set('q', opts.q);
  const qs = params.toString();
  return qs ? `/faq?${qs}` : '/faq';
}

export function faqHelpForPath(pathname: string): { initialQuery?: string; initialItemId?: string } {
  if (pathname === '/') return { initialItemId: 'saas-projects-list' };
  if (pathname === '/templates') return { initialItemId: 'saas-template-catalog' };
  if (pathname === '/orgs') return { initialItemId: 'saas-orgs-overview' };
  if (pathname === '/plans') return { initialItemId: 'saas-subscription-plans' };
  if (pathname === '/media') return { initialItemId: 'saas-media-library' };
  if (pathname === '/ai') return { initialItemId: 'saas-ai-site-structure' };
  if (pathname === '/settings') return { initialItemId: 'saas-settings-overview' };
  if (pathname === '/faq') return { initialItemId: 'saas-what-is-saas' };
  return {};
}
